import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, Copy, Link2, Clock, Users, Check } from 'lucide-react'
import api from '../../services/api'
import { dialog } from '../../services/dialog'
import { copyToClipboard } from '../../utils/clipboard'

interface Invite {
  id: string
  code: string
  maxUses: number | null
  uses: number
  expiresAt: string | null
  allowGuest: boolean
  createdAt: string
}

interface Props {
  channelId: string
  onClose: () => void
}

export default function InviteModal({ channelId, onClose }: Props) {
  const [invites, setInvites] = useState<Invite[]>([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [expiryMinutes, setExpiryMinutes] = useState(60)
  const [maxUses, setMaxUses] = useState<number | ''>('')
  const [allowGuest, setAllowGuest] = useState(true)
  // OSPROD-98: open the modal with a ready-to-share link — auto-create one
  // and copy it on first open. Ref guards StrictMode's double-effect so we
  // don't mint two invites per open.
  const didAutoCreateRef = useRef(false)

  useEffect(() => {
    fetchInvites()
  }, [channelId])

  useEffect(() => {
    if (didAutoCreateRef.current) return
    didAutoCreateRef.current = true
    createAndCopy()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchInvites = async () => {
    setLoading(true)
    try {
      const res = await api.get(`/channels/${channelId}/invites`)
      setInvites(res.data)
    } catch {
      // noop
    } finally {
      setLoading(false)
    }
  }

  const createInvite = async () => {
    setCreating(true)
    try {
      const payload: any = { allowGuest }
      if (expiryMinutes > 0) {
        payload.expiresAt = new Date(Date.now() + expiryMinutes * 60000).toISOString()
      }
      if (maxUses !== '') {
        payload.maxUses = Number(maxUses)
      }
      const res = await api.post(`/channels/${channelId}/invites`, payload)
      setInvites([res.data, ...invites])
    } catch (err: any) {
      void dialog.alert({ message: err.response?.data?.error || 'Failed to create invite', tone: 'destructive' })
    } finally {
      setCreating(false)
    }
  }

  const copyLink = async (code: string) => {
    const url = `${window.location.origin}/join/${code}`
    const ok = await copyToClipboard(url)
    if (ok) {
      setCopied(code)
      setTimeout(() => setCopied(null), 2000)
    }
  }

  // OSPROD-98: one-shot "open → ready link in clipboard". Creates a fresh
  // invite (default 60m, guests allowed) and copies its join link.
  const createAndCopy = async () => {
    setCreating(true)
    try {
      const payload = {
        allowGuest: true,
        expiresAt: new Date(Date.now() + 60 * 60000).toISOString(),
      }
      const res = await api.post(`/channels/${channelId}/invites`, payload)
      setInvites((prev) => [res.data, ...prev])
      await copyLink(res.data.code)
    } catch {
      // noop — the manual Generate button remains as a fallback
    } finally {
      setCreating(false)
    }
  }

  const deleteInvite = async (code: string) => {
    try {
      await api.delete(`/channels/${channelId}/invites/${code}`)
      setInvites(invites.filter((i) => i.code !== code))
    } catch {
      // noop
    }
  }

  const formatRemaining = (expiresAt: string | null) => {
    if (!expiresAt) return 'Never expires'
    const diff = new Date(expiresAt).getTime() - Date.now()
    if (diff <= 0) return 'Expired'
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `${mins}m left`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h left`
    return `${Math.floor(hours / 24)}d left`
  }

  const modalContent = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="w-[480px] max-w-[90vw] bg-card rounded-xl shadow-2xl border border-border p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Invite People</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition">
            <X size={20} />
          </button>
        </div>

        {/* Create new invite */}
        <div className="bg-card rounded-lg p-4 mb-4 space-y-3">
          <div className="text-sm font-medium text-muted-foreground">Create new invite link</div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground block mb-1">Expires after (minutes)</label>
              <input
                type="number"
                min={1}
                value={expiryMinutes}
                onChange={(e) => setExpiryMinutes(Number(e.target.value))}
                className="w-full bg-card text-foreground text-sm rounded px-3 py-2 border border-border focus:border-primary outline-none"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground block mb-1">Max uses</label>
              <input
                type="number"
                min={1}
                placeholder="Unlimited"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full bg-card text-foreground text-sm rounded px-3 py-2 border border-border focus:border-primary outline-none"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={allowGuest}
              onChange={(e) => setAllowGuest(e.target.checked)}
              className="rounded border-border"
            />
            Allow guests without account
          </label>
          <button
            onClick={createInvite}
            disabled={creating}
            className="w-full bg-primary hover:bg-primary/90 text-white text-sm font-medium py-2 rounded transition disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Generate Link'}
          </button>
        </div>

        {/* Active invites list */}
        <div className="space-y-2 max-h-[280px] overflow-y-auto">
          <div className="text-sm font-medium text-muted-foreground mb-1">Active invites</div>
          {loading ? (
            <div className="text-sm text-muted-foreground text-center py-4">Loading...</div>
          ) : invites.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-4">No active invites</div>
          ) : (
            invites.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center gap-3 bg-card rounded-lg px-3 py-2.5"
              >
                <Link2 size={16} className="text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground font-mono truncate">{invite.code}</div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {formatRemaining(invite.expiresAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={12} />
                      {invite.uses} / {invite.maxUses ?? '∞'}
                    </span>
                    {!invite.allowGuest && <span className="text-yellow-500">Members only</span>}
                  </div>
                </div>
                <button
                  onClick={() => copyLink(invite.code)}
                  className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition shrink-0"
                  title="Copy invite link"
                >
                  {copied === invite.code ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                </button>
                <button
                  onClick={() => deleteInvite(invite.code)}
                  className="p-1.5 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition shrink-0"
                  title="Revoke"
                >
                  <X size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
